import {Injectable} from "@angular/core";
import {
  HttpClient,
  HttpParams,
} from "@angular/common/http";
import {Paginator} from "../models/paginator";
import {Page} from "../models/page";

declare var $: any;

@Injectable({
  providedIn: 'root'
})
export class RestApiManager {

  constructor(
    private httpClient: HttpClient,
  ) { }

  async get<T>(type: new () => T, url: string, authToken?: string, params?: HttpParams | { [param: string]: any | any[]; }): Promise<T> {
    const json = await this.httpClient.get<T>(url, authToken ? {headers: this.getRestHeaders(authToken), params} : {}).toPromise();
    // @ts-ignore
    return type ? type['fromJson'](json) : json;
  }

  getFile(url: string, filename: string, authToken: string) {
    fetch(url, { headers: this.getRestHeaders(authToken) })
      .then(resp => resp.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        // the filename you want
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Something went wrong!'));
  }

  async getAll<T>(type: new () => T, url: string, authToken: string, options?: { paginator?: Paginator<T>, params?: HttpParams | { [param: string]: any | any[]; } }): Promise<T[]> {

    // Record new action for paginator
    if (options && options.paginator) {
      options.paginator._onFetch = async () => {

        if (options.paginator.onPageChangeStart) {
          options.paginator.onPageChangeStart();
        }

        if (options.paginator.onPageResults) {
          options.paginator.isLoading = true;
          // @ts-ignore
          const elems = await this.getAll<T>(type, url, authToken, options);
          options.paginator.currPageElements = elems.length;
          options.paginator.onPageResults(elems);
          options.paginator.isLoading = false;
        }

        if (options.paginator.onPageChangeEnd) {
          await options.paginator.onPageChangeEnd();
        }

      };
    }

    if (options && options.paginator && !options.paginator.ignorePagination) {

      // On page change start hook
      if (options.paginator.onPageChangeStart) {
        await options.paginator.onPageChangeStart();
      }

      const orderingParams = options && options.paginator && options.paginator.orderBy ? {
        sort: options.paginator.orderBy + (options.paginator.sorting ? `,${options.paginator.sorting.toLowerCase()}` : '')
      } : {
        sort: []
      };

      // Get all elements with pagination
      const page: Page<T> = await this.httpClient.get<Page<T>>(url, { headers: this.getRestHeaders(authToken), params: { ...options?.params, ...orderingParams, page: options?.paginator?.currentPage.toString(), size: options?.paginator?.pageSize.toString() } }).toPromise();
      options.paginator.totalElements = page.totalElements;

      // On page results hook
      if (options.paginator.onPageResults) {
        options.paginator.isLoading = true;
        // @ts-ignore
        const elems = page.content.map(x => type ? type['fromJson'](x) : x);
        options.paginator.currPageElements = elems.length;
        await options.paginator.onPageResults(elems);
        options.paginator.isLoading = false;
      }

      // On page change end hook
      if (options.paginator.onPageChangeEnd) {
        await options.paginator.onPageChangeEnd();
      }

      // @ts-ignore
      return page.content.map(x => type ? type['fromJson'](x) : x);

    } else if (options && options.paginator && options.paginator.ignorePagination) {

      // On page change start hook
      if (options.paginator.onPageChangeStart) {
        await options.paginator.onPageChangeStart();
      }

      const orderingParams = options && options.paginator && options.paginator.orderBy ? {
        sort: options.paginator.orderBy + (options.paginator.sorting ? `,${options.paginator.sorting.toLowerCase()}` : '')
      } : {
        sort: []
      };

      // Get all elements without pagination
      let elements: T[] = [];
      const firstPage: Page<T> = await this.httpClient.get<Page<T>>(url, { headers: this.getRestHeaders(authToken), params: { ...options?.params, ...orderingParams, size: options?.paginator?.pageSize.toString() } }).toPromise();
      elements = elements.concat(firstPage.content);
      const totPages = firstPage.totalPages;

      const pages = [];
      for (let i = 1; i < totPages; i++) {
        pages.push(this.httpClient.get<Page<T>>(url, { headers: this.getRestHeaders(authToken), params: { ...options?.params, ...orderingParams, page: i.toString(), size: options?.paginator?.pageSize.toString()  } }).toPromise());
      }
      (await Promise.all(pages)).forEach(page => elements = elements.concat(page.content));

      // On page results hook
      if (options.paginator.onPageResults) {
        options.paginator.isLoading = true;
        // @ts-ignore
        const elems = elements.map(x => type ? type['fromJson'](x) : x);
        options.paginator.currPageElements = elems.length;
        await options.paginator.onPageResults(elems);
        options.paginator.isLoading = false;
      }

      // On page change end hook
      if (options.paginator.onPageChangeEnd) {
        await options.paginator.onPageChangeEnd();
      }

      // @ts-ignore
      return elements.map(x => type ? type['fromJson'](x) : x);

    } else {

      // @ts-ignore
      return (await this.httpClient.get<T[]>(url, { headers: this.getRestHeaders(authToken), params: { ...options?.params } }).toPromise()).map(x => type ? type['fromJson'](x) : x);

    }

  }

  post<T>(url: string, data: any, authToken: string): Promise<T> {
    return this.httpClient.post<T>(url, data, { headers: this.getRestHeaders(authToken) }).toPromise();
  }

  put<T>(url: string, data: any, authToken: string): Promise<T> {
    return this.httpClient.put<T>(url, data, { headers: this.getRestHeaders(authToken) }).toPromise();
  }

  patch<T>(url: string, data: any, authToken: string): Promise<T> {
    return this.httpClient.patch<T>(url, data, { headers: this.getRestHeaders(authToken) }).toPromise();
  }

  delete(url: string, authToken: string): Promise<void> {
    return this.httpClient.delete<void>(url, { headers: this.getRestHeaders(authToken) }).toPromise();
  }

  async deleteWithBody(url: string, data: any, authToken: string): Promise<void> {
    return await $.ajax({
      type: 'DELETE',
      url: url,
      data,
      headers: this.getRestHeaders(authToken),
      async: false
    });
  }

  getRestHeaders(token: string) {
    return {
      'Authorization': `Bearer ${token}`
    };
  }

}
